import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener(
      'pointerleave',
      () => {
        // When the panel was opened by the "More" toggle it stays open until the
        // user closes it: with the toggle, only another click or Escape dismisses it.
        if (this.#isOverflowExpanded) return;
        this.#debouncedDeactivate();
      },
      {
        signal: this.#abortController.signal,
      }
    );

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#dismissController?.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Whether the overflow panel is currently held open by the "More" toggle.
   *
   * Read from the button rather than mirrored in a field: `#activateHandler` and
   * `#deactivate` both early-return on their own guards, which used to leave a
   * cached boolean disagreeing with the DOM. Once it said "open" with nothing
   * open, every later click took the close branch and the panel never came back.
   * @returns {boolean}
   */
  get #isOverflowExpanded() {
    return this.#moreButton?.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Listeners that dismiss the toggled-open overflow panel. Only live while it is open.
   * @type {AbortController | null}
   */
  #dismissController = null;

  /**
   * The overflow items as of the last open, so a reflow that actually changes the
   * set can be told from one that does not.
   * @type {Element[]}
   */
  #openOverflowItems = [];

  /**
   * The items currently living in the overflow slot.
   * @returns {Element[]}
   */
  #overflowItems() {
    return Array.from(this.querySelectorAll('[slot="overflow"]'));
  }

  /**
   * The "More" / "Less" toggle button. Only present while the list overflows.
   * @returns {HTMLElement | null}
   */
  get #moreButton() {
    return this.querySelector('[slot="more"] button.menu-list__link');
  }

  /**
   * Toggle the overflow panel open or closed, and swap the button between "More" and "Less".
   * Bound to the `[slot="more"]` item, so `event.target` is the list item the
   * activate/deactivate handlers already know how to resolve.
   * @param {Event} event
   */
  toggleOverflow = (event) => {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    if (this.#isOverflowExpanded) {
      this.#closeOverflow();
      return;
    }

    // Drop any stale active item first. Hovering inside the panel moves it, and a
    // close that bailed on one of its guards can leave it pointing at the very
    // link we are about to re-activate — which `#activateHandler` would then
    // treat as "already active" and return without opening anything.
    this.#state.activeItem = null;
    this.#activateHandler(event);

    // Only claim the open state if activation actually resolved an overflow item,
    // so the button can never be left reading "Less" over an empty panel.
    if (this.#state.activeItem) this.#setOverflowOpen(true);
  };

  /**
   * Close the overflow panel unconditionally. Unlike `#deactivate` this has no
   * guards to bail out on, so the toggle can always recover a consistent state
   * no matter how the panel was left.
   */
  #closeOverflow = () => {
    const previous = this.#state.activeItem;

    this.#setOverflowOpen(false);

    // Clear every overflow link, not just the tracked one: the panel is revealed
    // by `:has([slot="overflow"] [aria-expanded='true'])`, so a single stale
    // attribute anywhere in that slot would keep it visible.
    for (const link of this.querySelectorAll('[slot="overflow"] [aria-expanded="true"]')) {
      link.setAttribute('aria-expanded', 'false');
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';

    if (!previous) return;

    previous.setAttribute('data-animating', '');
    setTimeout(() => previous.removeAttribute('data-animating'), Math.max(0, this.animationDelay - 150));
  };

  /**
   * Reflect the open state on the toggle button and manage the dismiss listeners.
   * @param {boolean} open
   */
  #setOverflowOpen(open) {
    this.#moreButton?.setAttribute('aria-expanded', open.toString());

    this.#dismissController?.abort();
    this.#dismissController = null;

    if (!open) return;

    const controller = new AbortController();
    this.#dismissController = controller;
    const { signal } = controller;
    const close = this.#closeOverflow;

    // The opening click is handled during the capture phase, so by the time it
    // bubbles to the document the panel is open — `contains` keeps it from closing again.
    document.addEventListener(
      'click',
      (dismissEvent) => {
        if (dismissEvent.target instanceof Node && this.contains(dismissEvent.target)) return;
        close();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (dismissEvent) => {
        if (dismissEvent.key !== 'Escape') return;
        close();
        this.#moreButton?.focus();
      },
      { signal }
    );

    // A reflow that changes which items overflow, or drops the "More" button
    // altogether, would leave it stuck reading "Less" over a panel whose contents
    // have moved. OverflowList rewrites every item's `slot` on each reflow though,
    // even when the set is identical — and opening the panel itself triggers one.
    // Reacting to the attribute alone therefore closed the panel on the frame
    // after it opened, so compare the actual membership and ignore no-op reflows.
    const overflowList = this.refs.overflowMenu;

    if (!overflowList) return;

    this.#openOverflowItems = this.#overflowItems();

    const observer = new MutationObserver(() => {
      const current = this.#overflowItems();
      const unchanged =
        current.length === this.#openOverflowItems.length &&
        current.every((item, index) => item === this.#openOverflowItems[index]);

      if (unchanged) return;

      close();
    });

    observer.observe(overflowList, { attributes: true, attributeFilter: ['slot'], subtree: true });
    signal.addEventListener('abort', () => observer.disconnect());
  }

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    // Hovering a regular top-level item takes over the panel, so the "More"
    // toggle has to give up its open state along with its "Less" label.
    if (isDefaultSlot && this.#isOverflowExpanded) this.#setOverflowOpen(false);

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options] - `force` closes even while the pointer is
   *   still over the menu, which is what the "More" toggle and its dismiss listeners need.
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    if (this.#isOverflowExpanded) this.#setOverflowOpen(false);

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
